<script setup>
import { LoaderIcon, Share2Icon } from "lucide-vue-next";
import { computed } from "vue";

import useDrawer from "@/composables/drawer";
import ShareButton from "@campaign-js/Components/SocialButton.vue";
import { usePage } from "@inertiajs/vue3";

const props = defineProps({
  campaign: {
    type: Object,
    required: true
  },
  pullToRefresh: {
    type: Object,
    required: true
  },
  isLoggedIn: {
    type: Boolean,
    required: false,
    default: false
  },
  profileActions: {
    type: Array,
    required: false,
    default: () => []
  },
  customerActions: {
    type: Array,
    required: false,
    default: () => []
  }
});

const page = usePage();

const user = computed(() => page.props.user);

const { scale, isDragging, isRefreshing, refreshIconOpacity, canRefresh, showIcon } = props.pullToRefresh;

const drawer = useDrawer("campaign-editor");

const openBgEditTab = () => drawer.showTab(2);
</script>

<template>
  <div v-bind="$attrs" class="campaign-cover absolute inset-x-0 top-0 z-0 h-64" :class="{ 'has-photo': campaign.cover_photo_url }">
    <!-- Refresh Icon -->
    <div
      v-show="showIcon"
      class="absolute inset-0 z-10 flex items-start justify-center pt-4"
      :style="{
        opacity: refreshIconOpacity
      }"
    >
      <div>
        <LoaderIcon
          class="h-8 w-8 text-white drop-shadow-md transition-colors duration-200"
          :class="{ 'animate-spin': canRefresh || isRefreshing }"
        />
      </div>
    </div>

    <!-- Top Action Bar -->
    <div class="absolute left-0 right-0 top-0 z-20 flex items-center justify-between px-4 py-3">
      <div class="flex items-center space-x-2">
        <!-- This space can be used for a back button or logo if needed -->
      </div>

      <div class="flex items-center space-x-4">
        <ShareButton :campaign="campaign">
          <Share2Icon class="stroke-1.5 h-6 w-6 text-white drop-shadow-sm" />
        </ShareButton>
      </div>
    </div>

    <div class="relative h-full w-full overflow-hidden">
      <!-- Background container with scale transformation -->
      <div
        class="absolute inset-0 h-full w-full"
        :style="{
          transform: `scale(${scale})`,
          transition: isDragging ? 'none' : 'transform 200ms ease-out'
        }"
      >
        <!-- Enhanced Gradient Overlay -->
        <div
          class="pointer-events-none absolute inset-x-0 bottom-0 z-[11] h-full w-full select-none bg-gradient-to-t from-black/60 via-black/30 to-black/10"
        ></div>

        <img
          v-if="campaign.cover_photo_url"
          class="pointer-events-none h-full w-full select-none object-cover object-bottom"
          :src="campaign.cover_photo_url"
          alt="cover image"
        />
        <div v-else class="flex-center h-full w-full bg-campaign" @click="openBgEditTab"></div>
      </div>

      <!-- Centered Profile Photo and Info (Not affected by scale) -->
      <div class="absolute inset-0 z-[12] flex flex-col items-center justify-center">
        <div class="flex flex-col items-center px-6">
          <img class="h-18 w-18 rounded-full object-cover shadow-lg" :src="campaign.profile_photo_url" alt="profile photo" />
          <h1 class="mt-4 text-center text-lg font-bold text-white drop-shadow-lg md:text-xl">
            {{ campaign.title }}
          </h1>
          <p v-if="campaign.message" class="mt-2 max-w-md text-center text-sm text-white/90 drop-shadow-md">
            {{ campaign.message }}
          </p>
        </div>
      </div>
    </div>
  </div>

  <div class="spacer h-64"></div>
</template>
